import React, { useState, useRef } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import PuppyCard from './PuppyCard'
import PuppyModal from './PuppyModal'
import puppies from '../data/puppies'
import './PuppyGrid.css'

const PuppyGrid = () => {
  const [selectedPuppy, setSelectedPuppy] = useState(null)
  const [filter, setFilter] = useState('all')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const filters = [
    { id: 'all', label: 'All Puppies', count: puppies.length },
    { id: 'available', label: 'Available', count: puppies.filter(p => p.status === 'Available').length },
    { id: 'reserved', label: 'Reserved', count: puppies.filter(p => p.status === 'Reserved').length },
    { id: 'sold', label: 'Sold', count: puppies.filter(p => p.status === 'Sold').length }
  ]

  const filteredPuppies = filter === 'all' 
    ? puppies 
    : puppies.filter(puppy => puppy.status.toLowerCase() === filter)

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  const handlePuppyClick = (puppy) => {
    setSelectedPuppy(puppy)
  }

  const handleCloseModal = () => {
    setSelectedPuppy(null)
  }

  return (
    <section id="puppies" className="puppies" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2>Our Beautiful Puppies</h2>
          <p>
            Meet our adorable Cane Corso puppies, each with their unique personality and characteristics.
            All puppies are health tested, vaccinated, and come with comprehensive health guarantees.
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          className="filter-tabs"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {filters.map((filterOption) => (
            <motion.button
              key={filterOption.id}
              className={`filter-tab ${filter === filterOption.id ? 'active' : ''}`}
              onClick={() => setFilter(filterOption.id)}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              {filterOption.label}
              <span className="count">({filterOption.count})</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Puppies Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            className="puppies-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            exit="hidden"
          >
            {filteredPuppies.map((puppy, index) => (
              <motion.div
                key={puppy.id}
                variants={itemVariants}
                layout
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <PuppyCard 
                  puppy={puppy} 
                  onClick={() => handlePuppyClick(puppy)}
                  index={index}
                />
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {filteredPuppies.length === 0 && (
          <motion.div
            className="no-puppies"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h3>No puppies found</h3>
            <p>There are currently no puppies matching your selected filter.</p>
          </motion.div>
        )}

        {/* Quick Stats */}
        <motion.div
          className="puppy-stats"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="stat-item">
            <span className="stat-number">{puppies.filter(p => p.status === 'Available').length}</span>
            <span className="stat-label">Available Now</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{puppies.filter(p => p.status === 'Reserved').length}</span>
            <span className="stat-label">Reserved</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{puppies.filter(p => p.status === 'Sold').length}</span>
            <span className="stat-label">Happy Homes</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">100%</span>
            <span className="stat-label">Health Guaranteed</span>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="cta-section"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3>Ready to Welcome Your New Family Member?</h3>
          <p>Contact us today to learn more about our available puppies and our adoption process.</p>
          <Link to="/contact">
            <motion.button
              className="btn btn-primary btn-lg"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              Contact Us Today
              <motion.svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </motion.svg>
            </motion.button>
          </Link>
        </motion.div>
      </div>

      {/* Puppy Modal */}
      <AnimatePresence>
        {selectedPuppy && (
          <PuppyModal 
            puppy={selectedPuppy} 
            onClose={handleCloseModal}
          />
        )}
      </AnimatePresence>
    </section>
  )
}

export default PuppyGrid